import React, { useReducer, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Space, Form, Input, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import throttle from 'lodash/throttle';

import { Table, ApplicationUIContext } from '@yuhuinc/ui-library';

import { searchIncentives } from '../../store/actions';

import { getTranslatedString } from 'helpers/i18n';

const COLUMNS = [
  {
    title: getTranslatedString('landlordPortal.incentives.internalName'),
    dataIndex: 'internal_name',
    key: 'internal_name',
  },
  {
    title: getTranslatedString('landlordPortal.incentives.internalCode'),
    dataIndex: 'internal_code',
    key: 'internal_code',
  },
];

const StyledSpace = styled(Space)`
  width: 100%;

  > * {
    flex: 1;
  }
`;

const throttledSearchIncentives = throttle(searchIncentives, 300, { trailing: false });

const initialState = {
  fetchingFoundIncentives: false,
  foundIncentives: [],
  selectedIncentive: undefined,
};

function reducer(state, [action, payload]) {
  switch (action) {
    case 'SET_FETCHING':
      return { ...state, fetchingFoundIncentives: payload };
    case 'SET_FOUND_INCENTIVES':
      return { ...state, fetchingFoundIncentives: false, foundIncentives: payload };
    case 'SET_SELECTED_INCENTIVE':
      return { ...state, selectedIncentive: payload };
    default:
      return state;
  }
}

const SelectIncentive = ({ selectedUnit, initialValues, onSelect, onCancel }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const additionalInformationForm = Form.useFormInstance();
  const leaseTerm = Form.useWatch('lease_term', additionalInformationForm);
  const applicationSelectedIncentives = additionalInformationForm.getFieldValue('incentives');
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  // TODO - fetch incentives after unit, lease_term and lease dates selected
  const [{ fetchingFoundIncentives, foundIncentives, selectedIncentive }, dispatch] = useReducer(
    reducer,
    initialState
  );
  const incentivesDataSource = foundIncentives
    .filter(
      // Don't allow selection of incentives already selected.
      (incentive) =>
        !applicationSelectedIncentives.some(
          (selectedIncentive) => selectedIncentive.id === incentive.id
        )
    )
    .map((incentive) => ({
      key: incentive.id,
      id: incentive.id,
      incentive_type: incentive.incentive_type,
      internal_code: incentive.internal_code,
      internal_name: incentive.internal_name,
      internal_description: incentive.internal_description,
      benefits: incentive.benefits,
    }));

  useEffect(() => {
    async function fetchIncentives() {
      const params = {
        unitId: selectedUnit.id,
        leaseTerm: leaseTerm,
      };

      if (!values || !leaseTerm) {
        return;
      }

      if (values.query) {
        params.query = values.query;
      }

      dispatch(['SET_FETCHING', true]);
      const results = await throttledSearchIncentives(params);
      dispatch(['SET_FOUND_INCENTIVES', results.incentive_templates]);
    }

    fetchIncentives();
  }, [selectedUnit.id, values, leaseTerm]);

  function onSelectRow(_, selectedRows) {
    dispatch(['SET_SELECTED_INCENTIVE', selectedRows[0]]);
  }

  function onSubmit() {
    onSelect({
      id: selectedIncentive.id,
      name: selectedIncentive.internal_name,
      internal_name: selectedIncentive.internal_name,
      internal_code: selectedIncentive.internal_code,
    });
  }

  return (
    <Form layout="vertical" size={isMobile && 'large'} form={form} initialValues={initialValues}>
      <StyledSpace direction="vertical" size="middle">
        <Form.Item name="query">
          <Input suffix={<SearchOutlined />} />
        </Form.Item>
        <Table
          dataSource={incentivesDataSource}
          columns={COLUMNS}
          hideActionAndSearch
          loading={fetchingFoundIncentives}
          size={isMobile ? 'middle' : 'small'}
          rowSelection={{
            type: 'radio',
            fixed: true,
            onChange: onSelectRow,
            getCheckboxProps: (record) => ({
              disabled: !!applicationSelectedIncentives.find(
                (incentive) => incentive.id === record.id
              ),
            }),
          }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{
            x: isMobile,
          }}
        />
        <Space>
          <Button type="primary" htmlType="submit" disabled={!selectedIncentive} onClick={onSubmit}>
            {getTranslatedString('landlordPortal.applicationManagement.inviteLeadToApply.add')}
          </Button>
          <Button htmlType="button" onClick={onCancel}>
            {getTranslatedString('landlordPortal.applicationManagement.inviteLeadToApply.cancel')}
          </Button>
        </Space>
      </StyledSpace>
    </Form>
  );
};

SelectIncentive.defaultProps = {
  siteAndProperties: [],
  initialValues: {},
};

SelectIncentive.propTypes = {
  selectedUnit: PropTypes.any,
  initialValues: PropTypes.any,
  onSelect: PropTypes.func,
  onCancel: PropTypes.func,
};

export default SelectIncentive;
